// Internal Imports
import { DOCUMENTS } from "../../constants";
import { normaliseTableData } from "../../utils/tableUtils";
import { convertSignersData } from "../../utils/documentUtils";
// Destructuring
const { DOCUMENT_STATUS_CONFIG, DOCUMENT_COMPONENT_TYPES } = DOCUMENTS;
/**
 * generateDocumentDetails func filters out only necessary keys from API.
 * @param {Object} doc - Refers to the dealtable id to be fetched // "15"
 */
export const generateDocumentDetails = doc => {
  const details = {
    account: doc.account,
    created_by: doc.created_by,
    deadline_date: doc.deadline_date,
    dealTable: doc.deal_tables && doc.deal_tables[0],
    docNo: doc.doc_no,
    fields_used:
      doc.field_values &&
      doc.field_values.data &&
      Array.isArray(doc.field_values.data)
        ? doc.field_values.data
        : doc.fields_used || [],
    all_fields_used:
      doc.field_values &&
      doc.field_values.data &&
      Array.isArray(doc.field_values.data)
        ? doc.field_values.data
        : doc.fields_used || [],
    id: doc.id,
    isLocked: doc.is_locked,
    is_sent_for_signature: doc.is_sent_for_signature,
    is_signed: doc.is_signed,
    meta_values: doc.meta_values,
    meta_data: doc.meta_data || {},
    name: doc.title,
    docType: doc.document_type,
    opportunity: doc.opportunity,
    quoteTable: doc.quotes && doc.quotes[0],
    revisionNo: doc.revision_no,
    showForm: doc.show_form || false,
    stage: doc.stage,
    status: doc.status,
    documentStatus: doc.document_status,
    documentSettings: doc.document_settings,
    documentTag: doc.document_tag,
    template_id: doc.template_id,
    template_type: doc.template_type,
    scope: doc.scope,
    objectId: doc.object_id,
    image_base_url: doc.image_base_url,
    document_flow: doc.document_flow,
    signatories:
      doc.signatories &&
      doc.signatories.map(signer => ({
        ...signer,
        signers: convertSignersData(signer.signers),
      })),
    signatoriesV2: doc.signatories_v2,
    isPdfDocument: doc.is_pdf_document,
    pdfUrl: `${doc.image_base_url}${doc.doc_pdf_path}`,
    inodeId: doc.inode_id,
    documentIntent: doc.document_intent,
    paymentStatus: doc.payment_status,
  };
  return details;
};

export const updateGroupId = (components, newData) => {
  const dealtableComponentId = Object.keys(components).find(
    componentId => components[componentId].content.type === "DealTable",
  );

  if (dealtableComponentId) {
    const component = components[dealtableComponentId];
    const { data } = component.content;
    if (
      component.content.dealtableView === "GROUPED_SUMMARY" &&
      data.groupSummary &&
      Array.isArray(data.groupSummary.mergeGroupMapping)
    ) {
      for (let m = 0; m < data.groupSummary.mergeGroupMapping.length; m += 1) {
        if (
          data.groupSummary.mergeGroupMapping[m].groupID !==
            newData.dealTable.groups[m].id &&
          (data.groupSummary.mergeGroupMapping[m].group_by_value ===
            newData.dealTable.groups[m].group_by_value ||
            data.groupSummary.mergeGroupMapping[m].group_by ===
              newData.dealTable.groups[m].group_by)
        ) {
          data.groupSummary.mergeGroupMapping[m].groupID =
            newData.dealTable.groups[m].id;
        }
      }
      component.content.data = data;
      components[dealtableComponentId] = component;
    }
  }
};

export const generateDocStatusConfigs = (status, stage) => {
  const docStatusConfigs = [];
  let tempSeqNo;
  DOCUMENT_STATUS_CONFIG.forEach(docStatus => {
    const tempObj = { ...docStatus };
    if (tempObj.stage === stage) {
      tempObj.status = status;
      tempSeqNo = tempObj.seqNo;
    } else {
      tempObj.status = "";
    }
    docStatusConfigs.push(tempObj);
  });

  if (tempSeqNo === 4 && status === "DOCUMENT_CLIENT_SIGNED") {
    docStatusConfigs.forEach(docSt => {
      if (docSt.seqNo === 2) {
        docSt.stage = "Review Passed";
      }
      docSt.className = "status-success";
    });
  } else {
    docStatusConfigs.forEach(docSt => {
      if (docSt.seqNo < tempSeqNo) {
        if (docSt.seqNo === 2) {
          docSt.stage = "Review Passed";
        }
        docSt.className = "status-success";
      }
    });
  }

  return docStatusConfigs;
};

export const checkQuoteTableWrapper = components =>
  Boolean(
    Array.isArray(components) &&
      components.find(
        rowComponent =>
          Array.isArray(rowComponent) &&
          rowComponent.find(
            component =>
              component.content.type === "QuoteTableWrapper" ||
              component.content.section_type === "QuoteTableWrapper",
          ),
      ),
  );

export const checkQuoteTable = components =>
  Boolean(
    Array.isArray(components) &&
      components.find(
        rowComponent =>
          Array.isArray(rowComponent) &&
          rowComponent.find(
            component =>
              component.content.type === "QuoteTable" ||
              component.content.section_type === "QuoteTable" ||
              component.content.type === "DealTable" ||
              component.content.section_type === "DealTable",
          ),
      ),
  );

const getTextFields = (componentMeta, data) => {
  const { entityMap: entityData } = data;
  const formFields = [];
  if (entityData && Object.keys(entityData).length > 0)
    Object.values(entityData).forEach(entity => {
      if (entity.data && entity.data.dynamicFieldId) {
        formFields.push({
          fieldId: entity.data.dynamicFieldId,
          occurenceId: entity.data.id,
          componentMeta,
        });
      }
    });
  return formFields;
};

const getHTMLFields = (componentMeta, section) => {
  const { data, clauseOptions } = section;
  let formFields = [];
  if (clauseOptions && clauseOptions.id) {
    formFields.push({ fieldId: clauseOptions.id, componentMeta });
  } else if (data && typeof data !== "string") {
    formFields = getTextFields({ ...componentMeta }, data);
  }
  return formFields;
};

const getImageFields = (componentMeta, section) => {
  const { clauseOptions } = section;
  const formFields = [];
  if (clauseOptions && clauseOptions.id) {
    formFields.push({ fieldId: clauseOptions.id, componentMeta });
  }
  return formFields;
};

const getTableFields = (componentMeta, section) => {
  const { data: tableData, clauseOptions } = section;
  const hiddenCols =
    tableData.attributes && Array.isArray(tableData.attributes.hiddenCols)
      ? tableData.attributes.hiddenCols
      : [];
  const { data: rowData } = normaliseTableData(
    tableData,
    DOCUMENT_COMPONENT_TYPES.TABLE,
    hiddenCols,
  );
  let formfields = [];
  rowData.forEach((row, rowIndex) => {
    const { data: columnData } = row;
    columnData.forEach((column, colIndex) => {
      const {
        data: { data: cellData },
      } = column;
      let cellFields = [];
      if (cellData && typeof cellData !== "string") {
        cellFields = getTextFields(
          {
            ...componentMeta,
            itemType: "Table",
            tableRow: rowIndex,
            tableCol: colIndex,
          },
          cellData,
        );
      }
      if (Array.isArray(cellFields) && cellFields.length > 0)
        formfields = [...formfields, ...cellFields];
    });
  });
  if (clauseOptions && clauseOptions.id) {
    formfields = [{ fieldId: clauseOptions.id, componentMeta }, ...formfields];
  }
  return formfields;
};

const getCoverpageFields = (componentMeta, component) => {
  const { data } = component;
  let formfields = [];
  const { layout, components } = data;
  if (Array.isArray(layout) && layout.length > 0) {
    layout.forEach(rowData => {
      if (Array.isArray(rowData) && rowData.length > 0) {
        rowData.forEach(sectionId => {
          const section = components[sectionId].content;
          // eslint-disable-next-line no-use-before-define
          const fields = getComponentFields(
            {
              ...componentMeta,
              sectionId,
              blockType: "Coverpage",
            },
            section,
          );
          if (Array.isArray(fields) && fields.length > 0) {
            formfields = [...formfields, ...fields];
          }
        });
      } else if (rowData && !Array.isArray(rowData)) {
        const section = components[rowData].content;
        // eslint-disable-next-line no-use-before-define
        const fields = getComponentFields(
          {
            ...componentMeta,
            section: section.id || componentMeta.id,
            blockType: "Coverpage",
          },
          section,
        );
        if (Array.isArray(fields) && fields.length > 0) {
          formfields = [...formfields, fields];
        }
      }
    });
  }
  return formfields;
};

const getHeaderFooterFields = (componentMeta, component) => {
  const { data } = component;
  const { header, footer } = data;
  const formfieldsObj = {};
  const { layout: hlayout, components: hcomponents } = header;
  if (Array.isArray(hlayout) && hlayout.length > 0) {
    let headerformfields = [];
    hlayout.forEach(rowData => {
      if (Array.isArray(rowData) && rowData.length > 0) {
        rowData.forEach(sectionId => {
          const section = hcomponents[sectionId].content;
          // eslint-disable-next-line no-use-before-define
          const fields = getComponentFields(
            {
              ...componentMeta,
              sectionId,
              blockType: "Header",
            },
            section,
          );
          if (Array.isArray(fields) && fields.length > 0) {
            headerformfields = [...headerformfields, ...fields];
          }
        });
      } else if (rowData && !Array.isArray(rowData)) {
        const section = hcomponents[rowData].content;
        // eslint-disable-next-line no-use-before-define
        const fields = getComponentFields(
          {
            ...componentMeta,
            sectionId: section.id || componentMeta.id,
            blockType: "Header",
          },
          section,
        );
        if (Array.isArray(fields) && fields.length > 0) {
          headerformfields = [...headerformfields, fields];
        }
      }
    });
    formfieldsObj.header = headerformfields;
  }

  const { layout: flayout, components: fcomponents } = footer;
  if (Array.isArray(flayout) && flayout.length > 0) {
    let footerformfields = [];
    flayout.forEach(rowData => {
      if (Array.isArray(rowData) && rowData.length > 0) {
        rowData.forEach(sectionId => {
          const section = fcomponents[sectionId].content;
          // eslint-disable-next-line no-use-before-define
          const fields = getComponentFields(
            {
              ...componentMeta,
              sectionId,
              blockType: "Footer",
            },
            section,
          );
          if (Array.isArray(fields) && fields.length > 0) {
            footerformfields = [...footerformfields, ...fields];
          }
        });
      } else if (rowData && !Array.isArray(rowData)) {
        const section = fcomponents[rowData].content;
        // eslint-disable-next-line no-use-before-define
        const fields = getComponentFields(
          {
            ...componentMeta,
            sectionId: section.id || componentMeta.id,
            blockType: "Footer",
          },
          section,
        );
        if (Array.isArray(fields) && fields.length > 0) {
          footerformfields = [...footerformfields, fields];
        }
      }
    });
    formfieldsObj.footer = footerformfields;
  }

  return formfieldsObj;
};

const getComponentFields = (componentMeta, component) => {
  let componentFields = [];
  if (component.type === DOCUMENT_COMPONENT_TYPES.HTML) {
    componentFields = getHTMLFields(componentMeta, component);
  } else if (component.type === DOCUMENT_COMPONENT_TYPES.IMAGE) {
    componentFields = getImageFields(componentMeta, component);
  } else if (component.type === DOCUMENT_COMPONENT_TYPES.TABLE) {
    componentFields = getTableFields(componentMeta, component);
  } else if (component.type === DOCUMENT_COMPONENT_TYPES.COVERPAGE) {
    componentFields = getCoverpageFields(componentMeta, component);
  } else if (component.type === DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER) {
    componentFields = getHeaderFooterFields(componentMeta, component);
  }
  return componentFields;
};

export const getFormFields = (components, fieldsUsed) => {
  let bodyformfields = [];
  let headerformfields = [];
  let footerformfields = [];
  let coverpageformfields = [];
  // get all the fields
  Object.keys(components).forEach(componentId => {
    const section = components[componentId]
      ? components[componentId].content
      : {};
    const componentMeta = {
      componentId,
      itemType: section.type,
      blockType: "Body",
    };
    const componentFields = getComponentFields(componentMeta, section);
    // For Coverpage
    if (
      section.type === DOCUMENT_COMPONENT_TYPES.COVERPAGE &&
      Array.isArray(componentFields) &&
      componentFields.length > 0
    ) {
      coverpageformfields = componentFields;
    }
    // For Header
    else if (
      section.type === DOCUMENT_COMPONENT_TYPES.HEADER_FOOTER &&
      Object.keys(componentFields).length > 0
    ) {
      headerformfields = componentFields.header;
      footerformfields = componentFields.footer;
    } else if (Array.isArray(componentFields) && componentFields.length > 0)
      bodyformfields = [...bodyformfields, ...componentFields];
  });
  let formfields = [];
  if (Array.isArray(coverpageformfields) && coverpageformfields.length > 0) {
    formfields = [...formfields, ...coverpageformfields];
  }
  if (Array.isArray(headerformfields) && headerformfields.length > 0) {
    formfields = [...formfields, ...headerformfields];
  }
  if (Array.isArray(bodyformfields) && bodyformfields.length > 0) {
    formfields = [...formfields, ...bodyformfields];
  }
  if (Array.isArray(footerformfields) && footerformfields.length > 0) {
    formfields = [...formfields, ...footerformfields];
  }
  // get all unique fields with occurences
  let arrangedFields = [];
  formfields.forEach(field => {
    const fieldDetails = fieldsUsed.find(df => df.id === field.fieldId);
    if (fieldDetails) {
      const isAvailable = arrangedFields.findIndex(
        df => df.id === field.fieldId,
      );
      if (isAvailable !== -1) {
        arrangedFields[isAvailable].occurrences = [
          ...arrangedFields[isAvailable].occurrences,
          { ...field.componentMeta, occurenceId: field.occurenceId },
        ];
      } else {
        arrangedFields = [
          ...arrangedFields,
          {
            ...fieldDetails,
            occurrences: [
              { ...field.componentMeta, occurenceId: field.occurenceId },
            ],
          },
        ];
      }
    }
  });
  return arrangedFields;
};

// This method will return the df's associated with clause
export const getFormFieldsInClause = (components, fieldsUsed) => {
  let formfields = [];
  Object.keys(components).forEach(componentId => {
    const section = components[componentId]
      ? components[componentId].content
      : {};
    const componentMeta = {
      componentId,
      itemType: section.type,
      blockType: "Body",
    };
    const { clauseOptions } = section;
    if (clauseOptions) {
      const { clauseValues, value } = clauseOptions;

      if (Array.isArray(clauseValues) && clauseValues.length > 0) {
        const tempFeilds = [];
        clauseValues.forEach(cl => {
          const fields = getTextFields(componentMeta, JSON.parse(cl.value));
          tempFeilds.push(...fields);
        });
        formfields = [...formfields, ...tempFeilds];
      } else if (value && typeof value === "string") {
        const fields = getTextFields(componentMeta, JSON.parse(value));
        formfields.push(...fields);
      }
    }
  });
  // get all unique fields with occurences
  let arrangedFields = [];
  formfields.forEach(field => {
    const fieldDetails = fieldsUsed.find(df => df.id === field.fieldId);
    if (fieldDetails) {
      const isAvailable = arrangedFields.findIndex(
        df => df.id === field.fieldId,
      );
      if (isAvailable !== -1) {
        arrangedFields[isAvailable].occurrences = [
          ...arrangedFields[isAvailable].occurrences,
          { ...field.componentMeta, occurenceId: field.occurenceId },
        ];
      } else {
        arrangedFields = [
          ...arrangedFields,
          {
            ...fieldDetails,
            occurrences: [
              { ...field.componentMeta, occurenceId: field.occurenceId },
            ],
          },
        ];
      }
    }
  });
  return arrangedFields;
};
